import type { ReactNode } from 'react'
import { PageBootSkeleton } from '@/components/layout/PageBootSkeleton'
import { EmptyVisual } from '@/components/shared/empty-states/EmptyVisual'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { useOrganization } from '@/hooks/useOrganization'

import type { FeatureFlagKeyType } from '@scrambl/shared'

interface FeatureFlagGuardProps {
  flagKey: FeatureFlagKeyType
  children: ReactNode
}

/**
 * Route guard that checks if a feature flag is enabled for the current organization.
 * When the flag is off it renders a "not enabled" state in place, so a pasted or
 * bookmarked link explains itself instead of silently bouncing to Home.
 */
export function FeatureFlagGuard({ flagKey, children }: FeatureFlagGuardProps) {
  const { isLoading, isError, refetch, checkFeatureFlag } = useFeatureFlags([
    flagKey,
  ])
  const { currentOrganization } = useOrganization()

  // A page in flight keeps the shape of the page it becomes, never a bare
  // centred spinner (design audit S12).
  if (isLoading) {
    return <PageBootSkeleton />
  }

  if (isError) {
    return (
      <EmptyVisual
        shape="icon"
        tone="quiet"
        line="Couldn't load this page"
        hint="Check your connection and try again."
        action={{ label: 'Try again', onClick: () => void refetch() }}
        fullScreen
      />
    )
  }

  if (!checkFeatureFlag(flagKey)) {
    const orgSlug = currentOrganization?.slug
    return (
      <EmptyVisual
        shape="icon"
        tone="locked"
        // Feature flags are switched on by Scrambl staff (PlatformAdminGuard),
        // not workspace admins, so "ask an admin" sent owners nowhere.
        line="Not turned on for your workspace"
        hint="Contact Scrambl support to turn it on."
        action={{
          label: 'Go home',
          href: orgSlug ? `/${orgSlug}/today` : '/organizations',
        }}
        fullScreen
      />
    )
  }

  return <>{children}</>
}
